import { List, Map } from "immutable";
import { Project, Area, Line, Hole, Item, Vertex } from "./export";
import { GraphInnerCycles, GeometryUtils, IDBroker } from "../utils/export";
import { fraLog } from "../utils/fra-debug";
import { Layer as LayerModel } from "../models";
import { MODE_IDLE, MODE_WAITING_DRAWING_SQUARE, MODE_DRAWING_SQUARE, UPDATE_DRAWING_SQUARE, MODE_DRAGGING_LINE, MODE_DRAGGING_VERTEX } from "../constants";

const GRAPH_VERTEX_MERGE_TOLERANCE = 15;

function verticesNear(a, b, tolerance = GRAPH_VERTEX_MERGE_TOLERANCE) {
  return (
    GeometryUtils.samePoints(a, b) ||
    Math.hypot(a.x - b.x, a.y - b.y) <= tolerance
  );
}

const sameSet = (set1, set2) =>
  set1.size === set2.size && set1.isSuperset(set2) && set1.isSubset(set2);

function pointInPolygonXY(x, y, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + 0.000001) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function cycleMetaFromVertexIds(state, layerID, cycle) {
  const coords = cycle
    .map((vertexID) => state.getIn(["scene", "layers", layerID, "vertices", vertexID]))
    .filter(Boolean)
    .map((v) => [v.x, v.y]);
  if (coords.length < 3) return null;
  const area = polygonAreaFromFlat(coords);
  const cx = coords.reduce((s, [x]) => s + x, 0) / coords.length;
  const cy = coords.reduce((s, [, y]) => s + y, 0) / coords.length;
  return { cycle, coords, area, cx, cy };
}

// Drop outer shells when 2+ smaller rooms sit inside (fixes merged 16m labels).
function filterSupersededCycles(state, layerID, cycles) {
  const metas = cycles
    .map((cycle) => cycleMetaFromVertexIds(state, layerID, cycle))
    .filter(Boolean);

  return metas
    .filter((self) => {
      let childCount = 0;
      let childAreaSum = 0;
      metas.forEach((other) => {
        if (other === self) return;
        if (other.area <= 0 || other.area >= self.area * 0.92) return;
        if (pointInPolygonXY(other.cx, other.cy, self.coords)) {
          childCount += 1;
          childAreaSum += other.area;
        }
      });
      return !(childCount >= 2 && childAreaSum > self.area * 0.35);
    })
    .map((m) => m.cycle);
}

function isEnvelopeShellArea(state, layerID, areaId) {
  const area = state.getIn(["scene", "layers", layerID, "areas", areaId]);
  if (!area) return false;

  const selfMeta = cycleMetaFromVertexIds(state, layerID, area.vertices);
  if (!selfMeta || selfMeta.area <= 0) return false;

  let childCount = 0;
  let largestChildArea = 0;
  let totalChildArea = 0;

  state.getIn(["scene", "layers", layerID, "areas"]).forEach((other, otherId) => {
    if (otherId === areaId) return;
    const otherMeta = cycleMetaFromVertexIds(state, layerID, other.vertices);
    if (!otherMeta || otherMeta.area <= 0 || otherMeta.area >= selfMeta.area * 0.92) {
      return;
    }
    if (pointInPolygonXY(otherMeta.cx, otherMeta.cy, selfMeta.coords)) {
      childCount += 1;
      largestChildArea = Math.max(largestChildArea, otherMeta.area);
      totalChildArea += otherMeta.area;
    }
  });

  return (
    childCount >= 2 &&
    selfMeta.area > largestChildArea * 1.25 &&
    totalChildArea > selfMeta.area * 0.35
  );
}

function dedupeCyclesByVertices(cycles) {
  const seen = new Set();
  const out = [];
  cycles.forEach((cycle) => {
    const key = cycle
      .map((v) => v)
      .sort()
      .join("|");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(cycle);
  });
  return out;
}

// Drop near-duplicate rooms (same place, ~same size) → stops double dims
// like "10.11m x 9.20m" stacked on "10.10m x 9.20m".
function dedupeNearDuplicateCycles(state, layerID, cycles) {
  const metas = cycles
    .map((cycle) => cycleMetaFromVertexIds(state, layerID, cycle))
    .filter(Boolean)
    .sort((a, b) => b.area - a.area);

  const kept = [];
  metas.forEach((self) => {
    const dup = kept.some((other) => {
      const areaRatio =
        Math.min(self.area, other.area) / Math.max(self.area, other.area);
      const dist = Math.hypot(self.cx - other.cx, self.cy - other.cy);
      const span = Math.sqrt(Math.max(self.area, other.area));
      return areaRatio > 0.92 && dist < Math.max(40, span * 0.12);
    });
    if (!dup) kept.push(self);
  });
  return kept.map((m) => m.cycle);
}

function dedupeGraphEdges(edges) {
  const seen = new Set();
  const out = [];
  edges.forEach((edge) => {
    if (!edge) return;
    const [a, b] = edge;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(edge);
  });
  return out;
}

function polygonAreaFromFlat(flatCoords) {
  const n = flatCoords.length >> 1;
  if (n < 3) return 0;

  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area +=
      flatCoords[2 * i] * flatCoords[2 * j + 1] -
      flatCoords[2 * j] * flatCoords[2 * i + 1];
  }
  return Math.abs(area / 2);
}

function closestPointOnWallSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    return {
      x: x1,
      y: y1,
      t: 0,
      dist: Math.hypot(px - x1, py - y1),
    };
  }
  const t = Math.max(
    0,
    Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy))
  );
  const x = x1 + t * dx;
  const y = y1 + t * dy;
  return { x, y, t, dist: Math.hypot(px - x, py - y) };
}

class Layer {
  static create(state, name, altitude) {
    let layerID = IDBroker.acquireID();
    name = name || `layer ${layerID}`;
    altitude = altitude || 0;

    let layer = new LayerModel({
      id: layerID,
      visibleName: name,
      name,
      altitude,
    });
    let currentLayerID = state.getIn(["scene", "selectedLayer"]);

    // Show floor below at half opacity for tracing (PDF 04/06/26 p.1)
    let currentLayer = state.getIn(["scene", "layers", currentLayerID]);
    currentLayer = currentLayer.set("opacity", 0.5);
    state = state.setIn(["scene", "layers", currentLayerID], currentLayer);

    state = state.setIn(["scene", "selectedLayer"], layerID);
    state = state.setIn(["scene", "layers", layerID], layer);

    return { updatedState: state };
  }

  static createBelow(state, name, altitude) {
    let layerID = IDBroker.acquireID();
    name = name || "basement";
    altitude = altitude || 0;

    let layer = new LayerModel({
      id: layerID,
      visibleName: name,
      name,
      altitude,
    });

    let layers = state.getIn(["scene", "layers"]);
    const firstLayerID = layers.keySeq().first();
    let firstLayer = layers.get(firstLayerID).set("opacity", 0.5);

    let newLayers = new Map().set(layerID, layer);
    layers.forEach((l, id) => {
      newLayers = newLayers.set(id, id === firstLayerID ? firstLayer : l);
    });

    state = state.setIn(["scene", "layers"], newLayers);
    state = state.setIn(["scene", "selectedLayer"], layerID);

    return { updatedState: state };
  }

  static select(state, layerID) {
    if (!state.get("alterate")) state = Project.unselectAll(state).updatedState;
    if (state.get("showAllLayers")) {
      // console.log("ssssssss")
      state = Project.selectAllLayers(state, false).updatedState;
    }
    let currentLayerID = state.getIn(["scene", "selectedLayer"]);
    if (currentLayerID !== layerID) {
      let currentLayer = state.getIn(["scene", "layers", currentLayerID]);
      currentLayer = currentLayer.set("opacity", 0);
      state = state.setIn(["scene", "layers", currentLayerID], currentLayer);
    }

    // FIX Bug 6: Set ALL non-selected layers to opacity=0 so they don't
    // show through in the download screen (changeLayerProperties sets them
    // to 0.3 which makes them semi-visible)
    state.getIn(["scene", "layers"]).forEach((layer, lID) => {
      if (lID !== layerID) {
        state = state.setIn(["scene", "layers", lID, "opacity"], 0);
      }
    });
    let selectedLayer = state.getIn(["scene", "layers", layerID]);
    selectedLayer = selectedLayer.set("opacity", 1);

    // FIX: Reset visible=true for all elements on the selected layer.
    // When switching floors, changeLayerProperties sets items/areas/holes
    // visible=false on the old floor. But the download screen dropdown only
    // calls sceneActions.selectLayer (not changeLayerProperties), so items
    // and areas on the new floor stay visible=false → symbols and dimensions
    // disappear. Fix: always reset visible=true when a layer is selected.
    if (selectedLayer.items && selectedLayer.items.size) {
      selectedLayer = selectedLayer.set(
        "items",
        selectedLayer.items.map(item => item.set("visible", true))
      );
    }
    if (selectedLayer.holes && selectedLayer.holes.size) {
      selectedLayer = selectedLayer.set(
        "holes",
        selectedLayer.holes.map(hole => hole.set("visible", true))
      );
    }
    if (selectedLayer.areas && selectedLayer.areas.size) {
      selectedLayer = selectedLayer.set(
        "areas",
        selectedLayer.areas.map(area => area.set("visible", true))
      );
    }

    state = state.setIn(["scene", "layers", layerID], selectedLayer);
    state = state.setIn(["scene", "selectedLayer"], layerID);

    // FIX: Reset mode and drawing/dragging support when switching layers.
    // This prevents the blank screen bug caused by stale drawing state.
    // BUT only reset if we are NOT in drawing/finishing modes.
    // IMPORTANT: Use Project.setMode(MODE_IDLE) not state.merge({mode}) so that
    // dimension flag is properly reset to true when needed (setMode has this logic).
    const currentMode = state.get("mode");
    const drawingModes = [
      "MODE_FINISHING_PLAN",
      "MODE_DRAWING_ITEM",
      "MODE_DRAWING_HOLE",
      "MODE_DRAWING_SQUARE",
      "MODE_DRAWING_LINE",
    ];
    if (!drawingModes.includes(currentMode)) {
      state = Project.setMode(state, MODE_IDLE).updatedState;
      state = state.merge({
        drawingSupport: new Map(),
        draggingSupport: new Map(),
        snapElements: new List(),
        activeSnapElement: null,
      });
    }

    return { updatedState: state };
  }

  static selectElement(state, layerID, elementPrototype, elementID) {
    state = state.setIn(
      ["scene", "layers", layerID, elementPrototype, elementID, "selected"],
      true
    );
    state = state.updateIn(
      ["scene", "layers", layerID, "selected", elementPrototype],
      (elems) => elems.push(elementID)
    );

    return { updatedState: state };
  }

  static unselect(state, layerID, elementPrototype, elementID) {
    state = state.setIn(
      ["scene", "layers", layerID, elementPrototype, elementID, "selected"],
      false
    );
    state = state.updateIn(
      ["scene", "layers", layerID, "selected", elementPrototype],
      (elems) => elems.filter((el) => el.id === elementID)
    );
    return { updatedState: state };
  }

  static unselectAll(state, layerID) {
    let { lines, holes, items, areas } = state.getIn([
      "scene",
      "layers",
      layerID,
    ]);

    if (lines)
      lines.forEach((line) => {
        state = Line.unselect(state, layerID, line.id).updatedState;
      });
    if (holes)
      holes.forEach((hole) => {
        state = Hole.unselect(state, layerID, hole.id).updatedState;
      });
    if (items)
      items.forEach((item) => {
        state = Item.unselect(state, layerID, item.id).updatedState;
      });
    if (areas)
      areas.forEach((area) => {
        state = Area.unselect(state, layerID, area.id).updatedState;
      });

    return { updatedState: state };
  }

  static setProperties(state, layerID, properties) {
    state = state.mergeIn(["scene", "layers", layerID], properties);
    state = state.updateIn(["scene", "layers"], (layers) =>
      layers.sort((a, b) =>
        a.altitude !== b.altitude ? a.altitude - b.altitude : a.order - b.order
      )
    );

    return { updatedState: state };
  }

  static remove(state, layerID) {
    state = state.removeIn(["scene", "layers", layerID]);

    state = state.setIn(
      ["scene", "selectedLayer"],
      state.scene.selectedLayer !== layerID
        ? state.scene.selectedLayer
        : state.scene.layers.first().id
    );

    return { updatedState: state };
  }

  static removeElement(state, layerID, elementPrototype, elementID) {
    state = state.deleteIn([
      "scene",
      "layers",
      layerID,
      elementPrototype,
      elementID,
    ]);

    return { updatedState: state };
  }

  static ensureSolidRoomFill(state, layerID) {
    state = state.updateIn(["scene", "layers", layerID, "areas"], (areas) =>
      areas.map((area) =>
        area
          .set("visible", true)
          .setIn(["properties", "opacity"], 100)
          .setIn(["properties", "patternColor"], "#ffffff")
      )
    );
    return { updatedState: state };
  }

  static detectAndUpdateAreas(state, layerID, options = {}) {
    const mode = state.get("mode");
    const hasSquarePreview = state.getIn(["draggingSupport", "squareLines"]);
    const splitVerts = state.get("splittedVertices");
    const hasActiveSplit =
      splitVerts &&
      !splitVerts.isEmpty() &&
      (mode === MODE_DRAGGING_LINE || mode === MODE_DRAGGING_VERTEX);
    const skipWeld =
      mode === MODE_WAITING_DRAWING_SQUARE ||
      mode === MODE_DRAGGING_LINE ||
      mode === MODE_DRAGGING_VERTEX ||
      hasActiveSplit ||
      ((mode === MODE_DRAWING_SQUARE || mode === UPDATE_DRAWING_SQUARE) &&
        hasSquarePreview) ||
      options.skipWeld === true;

    // Only skip stale-area prune while geometry is mid-drag. skipWeld alone
    // must NOT keep old rooms (that caused stacked duplicate dimension labels).
    const skipAreaPrune =
      mode === MODE_WAITING_DRAWING_SQUARE ||
      mode === MODE_DRAGGING_LINE ||
      mode === MODE_DRAGGING_VERTEX ||
      hasActiveSplit ||
      ((mode === MODE_DRAWING_SQUARE || mode === UPDATE_DRAWING_SQUARE) &&
        hasSquarePreview);

    // Always weld duplicate corner points before cycle detection.
    if (skipWeld) {
      state.getIn(["scene", "layers", layerID, "vertices"]).forEach((vertex) => {
        state = Layer.mergeEqualsVertices(state, layerID, vertex.id).updatedState;
      });
    }

    // Close T-junctions so internal walls create separate room boxes.
    // Skip while drawing/resizing a square room — fixes would break the preview.
    // Also skip on normal wall drag (options.skipWeld) — client: never auto-split.
    if (!skipWeld) {
      state = Layer.weldLayerForDetection(state, layerID);
    }

    let verticesArray = [];
    let linesArray;
    let vertexID_to_verticesArrayIndex = {};
    let verticesArrayIndex_to_vertexID = {};

    // Merge vertices at the same point so every closed wall box is detected.
    state.getIn(["scene", "layers", layerID, "vertices"]).forEach((vertex) => {
      let mergedIndex = verticesArray.findIndex(([vx, vy]) =>
        verticesNear({ x: vx, y: vy }, vertex)
      );

      if (mergedIndex === -1) {
        mergedIndex = verticesArray.length;
        verticesArray.push([vertex.x, vertex.y]);
      }

      vertexID_to_verticesArrayIndex[vertex.id] = mergedIndex;
      if (!verticesArrayIndex_to_vertexID[mergedIndex]) {
        verticesArrayIndex_to_vertexID[mergedIndex] = vertex.id;
      }
    });

    linesArray = state
      .getIn(["scene", "layers", layerID, "lines"])
      .map((line) => {
        const i0 = vertexID_to_verticesArrayIndex[line.vertices.get(0)];
        const i1 = vertexID_to_verticesArrayIndex[line.vertices.get(1)];
        if (i0 === undefined || i1 === undefined || i0 === i1) return null;
        return [i0, i1];
      })
      .filter((edge) => edge !== null);

    const graphEdges = dedupeGraphEdges(linesArray.toArray ? linesArray.toArray() : linesArray);

    let innerCyclesByVerticesArrayIndex = GraphInnerCycles.calculateInnerCycles(
      verticesArray,
      graphEdges
    );

    let innerCyclesByVerticesID = new List(innerCyclesByVerticesArrayIndex).map(
      (cycle) =>
        new List(
          cycle.map(
            (vertexIndex) => verticesArrayIndex_to_vertexID[vertexIndex]
          )
        )
    );
    // All area vertices should be ordered in counterclockwise order
    innerCyclesByVerticesID = innerCyclesByVerticesID.map((area) => {
      const coords = area
        .map((vertexID) =>
          state.getIn(["scene", "layers", layerID, "vertices", vertexID])
        )
        .filter(Boolean);
      if (coords.size < 3) return area;
      return GraphInnerCycles.isClockWiseOrder(coords) ? area.reverse() : area;
    });

    innerCyclesByVerticesID = List(
      dedupeNearDuplicateCycles(
        state,
        layerID,
        filterSupersededCycles(
          state,
          layerID,
          dedupeCyclesByVertices(innerCyclesByVerticesID.toArray())
        )
      )
    );

    let areaIDs = [];
    let areaID = null;
    let isHole = null;
    // Remove areas that no longer match a detected closed room.
    // Never delete rooms while dragging/splitting — geometry is temporarily open.
    if (!skipAreaPrune) {
      state.getIn(["scene", "layers", layerID, "areas"]).forEach((area) => {
        const areaInUse = innerCyclesByVerticesID.some((vertices) =>
          sameSet(vertices, area.vertices)
        );
        if (!areaInUse) {
          fraLog("DETECT", "Removing stale room area", {
            areaId: area.id,
            verts: area.vertices.size,
          });
          state = Area.remove(state, layerID, area.id).updatedState;
        }
      });
    }
    //add new areas
    innerCyclesByVerticesID.forEach((cycle, ind) => {
      let areaInUse = state
        .getIn(["scene", "layers", layerID, "areas"])
        .find((area) => sameSet(area.vertices, cycle));

      if (areaInUse) {
        areaIDs[ind] = areaInUse.id;
        areaID = areaInUse.id;
        state = state.setIn(
          ["scene", "layers", layerID, "areas", areaIDs[ind], "holes"],
          new List()
        );
        state = state.setIn(
          ["scene", "layers", layerID, "areas", areaIDs[ind], "vertices"],
          cycle
        );
        state = state.mergeIn(
          ["scene", "layers", layerID, "areas", areaIDs[ind]],
          {
            properties: areaInUse.properties,
            visible: true,
          }
        );
        state = state.setIn(
          ["scene", "layers", layerID, "areas", areaIDs[ind], "properties", "opacity"],
          100
        );
      } else {
        let areaVerticesCoords = cycle.map((vertexID) =>
          state.getIn(["scene", "layers", layerID, "vertices", vertexID])
        );
        let resultAdd = Area.add(
          state,
          layerID,
          "area",
          areaVerticesCoords,
          state.catalog
        );
        areaID = resultAdd.area.id;
        areaIDs[ind] = resultAdd.area.id;
        state = resultAdd.updatedState;
        state = state.setIn(
          ["scene", "layers", layerID, "areas", areaID, "visible"],
          true
        );
      }
    });

    // Build a relationship between areas and their coordinates
    let verticesCoordsForArea = areaIDs.map((id) => {
      let vertices = state
        .getIn(["scene", "layers", layerID, "areas", id])
        .vertices.map((vertexID) => {
          let { x, y } = state.getIn([
            "scene",
            "layers",
            layerID,
            "vertices",
            vertexID,
          ]);
          return new List([x, y]);
        });
      return { id, vertices };
    });

    // Wall-drawn rooms must stay independent — no automatic hole cutouts.
    // Nested room-in-room cutouts caused transparent rooms + missing dimensions.
    let i;
    for (i = 0; i < verticesCoordsForArea.length; i++) {
      state = state.setIn(
        [
          "scene",
          "layers",
          layerID,
          "areas",
          verticesCoordsForArea[i].id,
          "holes",
        ],
        new List()
      );
    }

    // Every room: visible + solid white fill (including small split rooms).
    // Drop areas whose vertex IDs no longer resolve (causes half-transparent fills).
    const brokenAreaIds = [];
    state.getIn(["scene", "layers", layerID, "areas"]).forEach((area, areaId) => {
      const liveVerts = area.vertices.filter((vid) =>
        state.hasIn(["scene", "layers", layerID, "vertices", vid])
      );
      if (liveVerts.size < 3) {
        brokenAreaIds.push(areaId);
        return;
      }
      if (liveVerts.size !== area.vertices.size) {
        state = state.setIn(
          ["scene", "layers", layerID, "areas", areaId, "vertices"],
          liveVerts
        );
      }
      state = state.setIn(
        ["scene", "layers", layerID, "areas", areaId, "visible"],
        true
      );
      state = state.setIn(
        ["scene", "layers", layerID, "areas", areaId, "properties", "opacity"],
        100
      );
      state = state.setIn(
        ["scene", "layers", layerID, "areas", areaId, "properties", "patternColor"],
        "#ffffff"
      );
    });
    brokenAreaIds.forEach((areaId) => {
      state = Area.remove(state, layerID, areaId).updatedState;
    });

    // Remove ghost outer shells — they paint white over split rooms without labels.
    const envelopeAreaIds = [];
    state.getIn(["scene", "layers", layerID, "areas"]).forEach((area, areaId) => {
      if (isEnvelopeShellArea(state, layerID, areaId)) {
        envelopeAreaIds.push(areaId);
      }
    });
    envelopeAreaIds.forEach((areaId) => {
      state = Area.remove(state, layerID, areaId).updatedState;
    });

    const finalAreas = state.getIn(["scene", "layers", layerID, "areas"]);
    const areaDebugList = [];
    finalAreas.forEach((area, areaId) => {
      const xs = [];
      const ys = [];
      area.vertices.forEach((vid) => {
        const v = state.getIn(["scene", "layers", layerID, "vertices", vid]);
        if (v) {
          xs.push(v.x);
          ys.push(v.y);
        }
      });
      const w = xs.length ? (Math.max(...xs) - Math.min(...xs)) / 100 : 0;
      const h = ys.length ? (Math.max(...ys) - Math.min(...ys)) / 100 : 0;
      areaDebugList.push({
        id: areaId,
        verts: area.vertices.size,
        approxM: `${w.toFixed(2)} x ${h.toFixed(2)}`,
        visible: area.visible,
      });
    });

    fraLog("DETECT", "Room detection finished", {
      mode,
      skipWeld,
      skipAreaPrune,
      wallCount: state.getIn(["scene", "layers", layerID, "lines"]).size,
      vertexCount: state.getIn(["scene", "layers", layerID, "vertices"]).size,
      mergedGraphVertices: verticesArray.length,
      graphEdges: linesArray.size,
      cyclesFound: innerCyclesByVerticesID.size,
      areasInState: finalAreas.size,
      rooms: areaDebugList,
    });

    if (innerCyclesByVerticesID.size > finalAreas.size) {
      fraLog(
        "DETECT",
        "WARNING: graph found more cycles than areas saved — check merges"
      );
    }
    if (innerCyclesByVerticesID.size < finalAreas.size) {
      fraLog(
        "DETECT",
        "WARNING: more areas in state than cycles — possible overlap/ghost rooms"
      );
    }

    return {
      updatedState: state,
      areaID: areaID,
      newArea: innerCyclesByVerticesID.size > 0,
      isHole,
    };
  }

  static removeDuplicateLines(state, layerID) {
    const seen = new Set();
    let updatedState = state;

    state.getIn(["scene", "layers", layerID, "lines"]).forEach((line) => {
      const v0 = updatedState.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        line.vertices.get(0),
      ]);
      const v1 = updatedState.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
        line.vertices.get(1),
      ]);
      if (!v0 || !v1) return;

      const keyA = `${v0.x},${v0.y}|${v1.x},${v1.y}`;
      const keyB = `${v1.x},${v1.y}|${v0.x},${v0.y}`;
      const key = keyA < keyB ? keyA : keyB;

      if (seen.has(key)) {
        updatedState = Line.remove(updatedState, layerID, line.id).updatedState;
      } else {
        seen.add(key);
      }
    });

    return { updatedState };
  }

  static removeZeroLengthLines(state, layerID) {
    let updatedState = state
      .getIn(["scene", "layers", layerID, "lines"])
      .reduce((newState, line) => {
        let v_id0 = line.getIn(["vertices", 0]);
        let v_id1 = line.getIn(["vertices", 1]);

        let v0 = newState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          v_id0,
        ]);
        let v1 = newState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          v_id1,
        ]);

        if (!v0 || !v1) {
          if (v0) {
            newState = Vertex.removeElement(
              newState,
              layerID,
              v_id0,
              "lines",
              line.id
            ).updatedState;
          }
          if (v1) {
            newState = Vertex.removeElement(
              newState,
              layerID,
              v_id1,
              "lines",
              line.id
            ).updatedState;
          }
          newState = Layer.removeElement(
            newState,
            layerID,
            "lines",
            line.id
          ).updatedState;
        } else if (GeometryUtils.verticesDistance(v0, v1) === 0) {
          newState = Line.remove(newState, layerID, line.id).updatedState;
        }

        return newState;
      }, state);

    return { updatedState };
  }

  static snapPointToNearestWall(state, layerID, px, py, tolerance = 70) {
    let bestDist = tolerance;
    let best = { x: px, y: py };
    const vertices = state.getIn(["scene", "layers", layerID, "vertices"]);

    vertices.forEach((v) => {
      const d = GeometryUtils.pointsDistance(px, py, v.x, v.y);
      if (d < bestDist) {
        bestDist = d;
        best = { x: v.x, y: v.y };
      }
    });

    state.getIn(["scene", "layers", layerID, "lines"]).forEach((hostLine) => {
      const h0 = vertices.get(hostLine.vertices.get(0));
      const h1 = vertices.get(hostLine.vertices.get(1));
      if (!h0 || !h1) return;

      const hit = closestPointOnWallSegment(px, py, h0.x, h0.y, h1.x, h1.y);
      if (hit.dist < bestDist) {
        bestDist = hit.dist;
        best = { x: hit.x, y: hit.y };
      }
    });

    return best;
  }

  static splitHostWallsAtPoint(state, layerID, px, py, tolerance = 80) {
    let updatedState = state;
    let guard = 0;

    while (guard < 16) {
      guard += 1;
      const lines = updatedState.getIn(["scene", "layers", layerID, "lines"]);
      let split = false;

      lines.some((hostLine) => {
        if (
          !updatedState.hasIn([
            "scene",
            "layers",
            layerID,
            "lines",
            hostLine.id,
          ])
        ) {
          return false;
        }

        const h0 = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          hostLine.vertices.get(0),
        ]);
        const h1 = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          hostLine.vertices.get(1),
        ]);
        if (!h0 || !h1) return false;

        if (
          GeometryUtils.samePoints({ x: px, y: py }, h0) ||
          GeometryUtils.samePoints({ x: px, y: py }, h1)
        ) {
          return false;
        }

        if (
          !GeometryUtils.isPointOnLineSegment(
            h0.x,
            h0.y,
            h1.x,
            h1.y,
            px,
            py,
            tolerance
          )
        ) {
          return false;
        }

        const offset = GeometryUtils.pointPositionOnLineSegment(
          h0.x,
          h0.y,
          h1.x,
          h1.y,
          px,
          py
        );
        if (offset <= 0.02 || offset >= 0.98) return false;

        const splitResult = Line.split(
          updatedState,
          layerID,
          hostLine.id,
          px,
          py,
          { skipSnap: true }
        );
        if (splitResult && splitResult.updatedState) {
          updatedState = splitResult.updatedState;
          split = true;
        }
        return true;
      });

      if (!split) break;

      updatedState
        .getIn(["scene", "layers", layerID, "vertices"])
        .forEach((vertex) => {
          const merged = Layer.mergeEqualsVertices(
            updatedState,
            layerID,
            vertex.id
          );
          if (merged && merged.updatedState) {
            updatedState = merged.updatedState;
          }
        });
    }

    return { updatedState };
  }

  static ensureRectangularAreaFromCorners(state, layerID, cornerVertexIDs) {
    if (!cornerVertexIDs || cornerVertexIDs.size !== 4) {
      return state;
    }

    const cycle = cornerVertexIDs;
    const verts = cycle
      .map((id) => state.getIn(["scene", "layers", layerID, "vertices", id]))
      .filter(Boolean);
    if (verts.size !== 4) return state;

    const exists = state
      .getIn(["scene", "layers", layerID, "areas"])
      .some((area) => sameSet(area.vertices, cycle));
    if (exists) return state;

    const result = Area.add(state, layerID, "area", verts, state.catalog);
    state = result.updatedState;
    return state.setIn(
      ["scene", "layers", layerID, "areas", result.area.id, "visible"],
      true
    );
  }

  static weldLayerForDetection(state, layerID) {
    let updatedState = state;

    updatedState
      .getIn(["scene", "layers", layerID, "vertices"])
      .forEach((vertex) => {
        updatedState = Layer.mergeEqualsVertices(
          updatedState,
          layerID,
          vertex.id
        ).updatedState;
      });

    const endpointKeys = new Set();
    updatedState.getIn(["scene", "layers", layerID, "lines"]).forEach((line) => {
      [0, 1].forEach((endpointIndex) => {
        const vertex = updatedState.getIn([
          "scene",
          "layers",
          layerID,
          "vertices",
          line.vertices.get(endpointIndex),
        ]);
        if (!vertex) return;

        const key = `${Math.round(vertex.x)}:${Math.round(vertex.y)}`;
        if (endpointKeys.has(key)) return;
        endpointKeys.add(key);

        updatedState = Layer.splitHostWallsAtPoint(
          updatedState,
          layerID,
          vertex.x,
          vertex.y
        ).updatedState;
      });
    });

    for (let pass = 0; pass < 20; pass++) {
      updatedState
        .getIn(["scene", "layers", layerID, "vertices"])
        .forEach((vertex) => {
          updatedState = Layer.mergeEqualsVertices(
            updatedState,
            layerID,
            vertex.id
          ).updatedState;
        });

      const linesBefore = updatedState.getIn([
        "scene",
        "layers",
        layerID,
        "lines",
      ]).size;
      updatedState = Layer.connectLineEndpointsToWalls(updatedState, layerID, 70);
      updatedState = Layer.splitLinesAtInteriorVertices(updatedState, layerID);
      updatedState = Layer.removeZeroLengthLines(updatedState, layerID).updatedState;
      updatedState = Layer.removeDuplicateLines(updatedState, layerID).updatedState;
      updatedState
        .getIn(["scene", "layers", layerID, "vertices"])
        .forEach((vertex) => {
          updatedState = Layer.mergeEqualsVertices(
            updatedState,
            layerID,
            vertex.id
          ).updatedState;
        });

      if (
        updatedState.getIn(["scene", "layers", layerID, "lines"]).size ===
        linesBefore
      ) {
        break;
      }
    }

    return updatedState;
  }

  static connectLineEndpointsToWalls(state, layerID, tolerance = 70) {
    let updatedState = state;
    let changed = true;
    let guard = 0;

    while (changed && guard < 32) {
      changed = false;
      guard += 1;

      const lines = updatedState.getIn(["scene", "layers", layerID, "lines"]);
      const vertices = updatedState.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
      ]);

      let didWork = false;

      lines.some((line) => {
        return [0, 1].some((endpointIndex) => {
          const vertexId = line.vertices.get(endpointIndex);
          const vertex = vertices.get(vertexId);
          if (!vertex) return false;

          return lines.some((hostLine) => {
            if (hostLine.id === line.id) return false;
            if (
              !updatedState.hasIn([
                "scene",
                "layers",
                layerID,
                "lines",
                hostLine.id,
              ])
            ) {
              return false;
            }

            const h0Id = hostLine.vertices.get(0);
            const h1Id = hostLine.vertices.get(1);
            if (vertexId === h0Id || vertexId === h1Id) return false;

            const h0 = vertices.get(h0Id);
            const h1 = vertices.get(h1Id);
            if (!h0 || !h1) return false;

            if (
              !GeometryUtils.isPointOnLineSegment(
                h0.x,
                h0.y,
                h1.x,
                h1.y,
                vertex.x,
                vertex.y,
                tolerance
              )
            ) {
              return false;
            }

            const offset = GeometryUtils.pointPositionOnLineSegment(
              h0.x,
              h0.y,
              h1.x,
              h1.y,
              vertex.x,
              vertex.y
            );
            if (offset <= 0.02 || offset >= 0.98) return false;

            updatedState = Line.split(
              updatedState,
              layerID,
              hostLine.id,
              vertex.x,
              vertex.y,
              { skipSnap: true }
            ).updatedState;
            didWork = true;
            return true;
          });
        });
      });

      if (didWork) changed = true;
    }

    return updatedState;
  }

  static snapEndpointsNearWalls(state, layerID) {
    return state;
  }

  static splitLinesAtInteriorVertices(state, layerID) {
    let updatedState = state;
    let changed = true;
    let guard = 0;

    while (changed && guard < 64) {
      changed = false;
      guard += 1;

      const lines = updatedState.getIn(["scene", "layers", layerID, "lines"]);
      const vertices = updatedState.getIn([
        "scene",
        "layers",
        layerID,
        "vertices",
      ]);

      let splitLineId = null;
      let splitX = null;
      let splitY = null;

      lines.some((line) => {
        const v0 = vertices.get(line.vertices.get(0));
        const v1 = vertices.get(line.vertices.get(1));
        if (!v0 || !v1) return false;

        return vertices.some((vertex) => {
          if (vertex.id === v0.id || vertex.id === v1.id) return false;
          if (
            !GeometryUtils.isPointOnLineSegment(
              v0.x,
              v0.y,
              v1.x,
              v1.y,
              vertex.x,
              vertex.y,
              70
            )
          ) {
            return false;
          }

          const offset = GeometryUtils.pointPositionOnLineSegment(
            v0.x,
            v0.y,
            v1.x,
            v1.y,
            vertex.x,
            vertex.y
          );
          if (offset <= 0.015 || offset >= 0.985) return false;

          splitLineId = line.id;
          splitX = vertex.x;
          splitY = vertex.y;
          return true;
        });
      });

      if (splitLineId != null) {
        updatedState = Line.split(
          updatedState,
          layerID,
          splitLineId,
          splitX,
          splitY,
          { skipSnap: true }
        ).updatedState;
        changed = true;
      }
    }

    return updatedState;
  }

  static mergeEqualsVertices(state, layerID, vertexID) {
    //1. find vertices to remove
    let vertex = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      vertexID,
    ]);

    if (!vertex) return { updatedState: state };

    let doubleVertices = state
      .getIn(["scene", "layers", layerID, "vertices"])
      .filter((v) => {
        return (
          v &&
          v.id !== vertexID &&
          GeometryUtils.samePoints(vertex, v)
        );
      });

    if (doubleVertices.isEmpty()) return { updatedState: state };

    doubleVertices.forEach((doubleVertex) => {
      let reduced = doubleVertex.lines.reduce((reducedState, lineID) => {
        reducedState = reducedState.updateIn(
          ["scene", "layers", layerID, "lines", lineID, "vertices"],
          (vertices) => {
            if (vertices) {
              return vertices.map((v) =>
                v === doubleVertex.id ? vertexID : v
              );
            }
          }
        );
        reducedState = Vertex.addElement(
          reducedState,
          layerID,
          vertexID,
          "lines",
          lineID
        ).updatedState;

        return reducedState;
      }, state);

      let biReduced = doubleVertex.areas.reduce((reducedState, areaID) => {
        reducedState = reducedState.updateIn(
          ["scene", "layers", layerID, "areas", areaID, "vertices"],
          (vertices) => {
            if (vertices)
              return vertices.map((v) =>
                v === doubleVertex.id ? vertexID : v
              );
          }
        );
        reducedState = Vertex.addElement(
          reducedState,
          layerID,
          vertexID,
          "areas",
          areaID
        ).updatedState;

        return reducedState;
      }, reduced);

      state = Vertex.remove(
        biReduced,
        layerID,
        doubleVertex.id,
        null,
        null,
        true
      ).updatedState;
    });

    return { updatedState: state };
  }

  static setPropertiesOnSelected(state, layerID, properties) {
    let selected = state.getIn(["scene", "layers", layerID, "selected"]);

    selected.lines.forEach(
      (lineID) =>
        (state = Line.setProperties(
          state,
          layerID,
          lineID,
          properties
        ).updatedState)
    );
    selected.holes.forEach(
      (holeID) =>
        (state = Hole.setProperties(
          state,
          layerID,
          holeID,
          properties
        ).updatedState)
    );
    selected.areas.forEach(
      (areaID) =>
        (state = Area.setProperties(
          state,
          layerID,
          areaID,
          properties
        ).updatedState)
    );
    selected.items.forEach(
      (itemID) =>
        (state = Item.setProperties(
          state,
          layerID,
          itemID,
          properties
        ).updatedState)
    );

    return { updatedState: state };
  }

  static updatePropertiesOnSelected(state, layerID, properties) {
    let selected = state.getIn(["scene", "layers", layerID, "selected"]);

    selected.lines.forEach(
      (lineID) =>
        (state = Line.updateProperties(
          state,
          layerID,
          lineID,
          properties
        ).updatedState)
    );
    selected.holes.forEach(
      (holeID) =>
        (state = Hole.updateProperties(
          state,
          layerID,
          holeID,
          properties
        ).updatedState)
    );
    selected.areas.forEach(
      (areaID) =>
        (state = Area.updateProperties(
          state,
          layerID,
          areaID,
          properties
        ).updatedState)
    );
    selected.items.forEach(
      (itemID) =>
        (state = Item.updateProperties(
          state,
          layerID,
          itemID,
          properties
        ).updatedState)
    );

    return { updatedState: state };
  }

  static setAttributesOnSelected(state, layerID, attributes) {
    let selected = state.getIn(["scene", "layers", layerID, "selected"]);

    selected.lines.forEach(
      (lineID) =>
        (state = Line.setAttributes(
          state,
          layerID,
          lineID,
          attributes
        ).updatedState)
    );
    selected.holes.forEach(
      (holeID) =>
        (state = Hole.setAttributes(
          state,
          layerID,
          holeID,
          attributes
        ).updatedState)
    );
    selected.items.forEach(
      (itemID) =>
        (state = Item.setAttributes(
          state,
          layerID,
          itemID,
          attributes
        ).updatedState)
    );
    selected.areas.forEach(
      (areaID) =>
        (state = Area.setProperties(
          state,
          layerID,
          areaID,
          attributes
        ).updatedState)
    );

    return { updatedState: state };
  }
}

export { Layer as default };
